import './poultryDesign.css';
import Button from '../shared/Button.js';
// ============================================================================
// src/broiler/BroilerTimelineView.jsx  —  Phase 2 Round 6
// ----------------------------------------------------------------------------
// Broiler + layer timeline Gantt view. Consumes contexts via hooks; Header
// is shared and openEdit remains local. The visible date range is derived from data:
// left bound = today − 90 days; right bound = latest rendered end date + 30d,
// extended to cover the desktop window. Desktop fits the current month and the
// next three into the available date canvas; later data remains scrollable.
// Phones retain the 120px/week scale and open near today.
// ============================================================================
import React, {useRef, useMemo} from 'react';
import {useAppNavigate as useNavigate} from '../app/navigation/index.js';
import {toISO, addDays, fmtDate as fmt, fmtDate as fmtS, todayISO} from '../lib/dateUtils.js';
import {S, getReadableText} from '../lib/styles.js';
import {getProgramColor} from '../lib/programColors.js';
import {
  calcTimeline,
  RESOURCES as RAW_RESOURCES,
  getBatchColor,
  getBatchDisplayColor,
  breedLabel,
} from '../lib/broiler.js';
import {timelineRowSeparation} from '../lib/broilerTimelineRows.js';
import {
  POULTRY_TIMELINE_GUTTER,
  poultryTimelineDay,
  poultryTimelineDate,
  poultryTimelineMonths,
  poultryTimelineViewport,
} from '../lib/poultryTimelineViewport.js';
import {usePhoneLayout} from '../shared/usePhoneLayout.js';
import {useBatches} from '../contexts/BatchesContext.jsx';
import {useLayer} from '../contexts/LayerContext.jsx';
import {useAuth} from '../contexts/AuthContext.jsx';
import {useUI} from '../contexts/UIContext.jsx';
import {RESOURCE} from '../app/data/routeDataCatalog.js';
import type {BroilerTimelineCoreData} from '../app/data/routeDataCatalog.js';
import {useRouteResource} from '../app/data/useRouteResource.js';
import Header from '../shared/Header.tsx';
import BatchForm from './BatchForm.tsx';

type BroilerTimelineBatch = {
  id?: string | number | null;
  name?: string | null;
  breed?: string | null;
  hatchDate?: string | null;
  processingDate?: string | null;
  brooder?: string | null;
  schooner?: string | null;
  brooderIn?: string | null;
  brooderOut?: string | null;
  schoonerIn?: string | null;
  schoonerOut?: string | null;
  hatchery?: string | null;
  growoutWeeks?: string | number | null;
  brooderDays?: string | number | null;
  birdCount?: string | number | null;
  conflictOverride?: boolean | null;
  status?: string | null;
  [key: string]: unknown;
};

type LayerTimelineBatch = {
  id?: string | number | null;
  name?: string | null;
  status?: string | null;
  original_count?: string | number | null;
  brooder_name?: string | null;
  brooder_entry_date?: string | null;
  brooder_exit_date?: string | null;
  schooner_name?: string | null;
  schooner_entry_date?: string | null;
  schooner_exit_date?: string | null;
  [key: string]: unknown;
};

type TimelineResource = {type: 'brooder' | 'schooner'; id: string; label: string};
type TimelineBar = {label: string; start: string | null; end: string | null; resName?: string | null};
type LayerTooltip = {
  type: 'layer';
  lbId?: string | number | null;
  barKey?: 'b' | 's';
  lb: LayerTimelineBatch;
  bar: TimelineBar;
  vx: number;
  vy: number;
};
type BatchTooltip = {
  type: 'brooder' | 'schooner';
  id?: string | number | null;
  batch: BroilerTimelineBatch;
  resType: 'brooder' | 'schooner';
  vx: number;
  vy: number;
};
type TimelineTooltip = LayerTooltip | BatchTooltip;
// `openEdit` is OPTIONAL, and the optionality is the release contract rather
// than defensive slack. BroilerEntry still passes App's batch-form opener on
// the legacy mount; the released mount in src/app/boot/releasedSurfaceRenderer
// only forwards the Add Batch action. `openBroilerBatch`
// below already guarded the call with `typeof openEdit === 'function'` before
// this surface was released, so the two mounts differ only in whether the
// nameless-batch bar has an edit affordance — and a nameless batch cannot be
// created, only produced by blanking the name of an existing one.
type BroilerTimelineProps = {
  openEdit?: (batch: unknown) => void;
  openAdd?: () => void;
  upd?: (key: string, value: string) => void;
  closeForm?: () => Promise<boolean>;
  submit?: (force: boolean) => Promise<boolean>;
  del?: (id: string, onDeleted?: () => void) => void;
  parseProcessorXlsx?: (file: File) => Promise<void>;
  persistDocuments?: (batchId: string | null, documents: unknown[]) => Promise<boolean>;
  embedded?: boolean;
};

const asBatchRows = (rows: readonly unknown[]): BroilerTimelineBatch[] => rows as BroilerTimelineBatch[];
const asLayerRows = (rows: readonly unknown[]): LayerTimelineBatch[] => rows as LayerTimelineBatch[];
// Preserve the source-level `RESOURCES.map` anchor while stating the literal
// resource contract at this view's boundary.
const RESOURCES = RAW_RESOURCES as TimelineResource[];

// Frozen module constant, not an inline object literal: useRouteResource
// serializes params into its key, and a fresh {} every render would rebuild the
// key and re-run everything downstream of it forever.
const BROILER_TIMELINE_PARAMS = Object.freeze({});

export default function BroilerTimelineView({
  openEdit,
  openAdd,
  upd,
  closeForm,
  submit,
  del,
  parseProcessorXlsx,
  persistDocuments,
  embedded = false,
}: BroilerTimelineProps) {
  const navigate = useNavigate();
  const {authState} = useAuth();
  const canAdd = authState && (authState.role === 'admin' || authState.role === 'management');
  const broilerAccent = getProgramColor('broiler');
  // ONLY the tooltip is still read off BatchesContext, and that is not a data
  // read: it is the hover-position object this view itself sets, held in the
  // provider because App's own former render body set it. The two DIRECTORIES —
  // the ppp-v4 blob and the layer batch table — were App's former-eager boot
  // results, and are read here through broiler.timeline.core so `timeline` can
  // retire from the frozen app_store and layer_batches consumer lists in
  // tests/fixtures/former-eager-dependencies.json. Do not destructure `batches`
  // or `layerBatches` off a provider hook again: that is one of the two
  // channels former_eager_evidence_static derives consumption from, and the
  // retirements would become false claims.
  const {tooltip: tooltipRaw, setTooltip, showForm, editId, setBatches} = useBatches();
  const {setLayerBatches} = useLayer();
  const timelineResource = useRouteResource(RESOURCE.BROILER_TIMELINE_CORE, BROILER_TIMELINE_PARAMS);
  const timelineData = timelineResource.data as BroilerTimelineCoreData | null;
  React.useLayoutEffect(() => {
    if (embedded || !timelineData) return;
    setBatches(timelineData.batches);
    setLayerBatches(timelineData.layerBatches);
  }, [embedded, timelineData, setBatches, setLayerBatches]);
  const batches = asBatchRows(timelineData?.batches || []);
  const layerBatches = asLayerRows(timelineData?.layerBatches || []);
  const tooltip = tooltipRaw as TimelineTooltip | null;
  const {setView, setPendingEdit} = useUI();

  function openBroilerBatch(b: BroilerTimelineBatch) {
    if (embedded) return;
    if (b && b.name) {
      navigate('/broiler/batches/' + encodeURIComponent(b.name));
    } else if (typeof openEdit === 'function') {
      openEdit(b);
    }
  }

  const today = todayISO();
  const tlStart = toISO(addDays(today, -90));
  const phone = usePhoneLayout();
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [contentWidth, setContentWidth] = React.useState(0);
  const scrollGeometry = useRef<{start: string; dayWidth: number} | null>(null);
  const viewedDay = useRef<number | null>(null);

  const latestEndISO = useMemo(() => {
    let latest = today;
    const bump = (iso: string | null | undefined) => {
      if (iso && iso > latest) latest = iso;
    };
    for (const b of batches) {
      bump(b.brooderOut);
      bump(b.schoonerOut);
      bump(b.processingDate);
      // Cover planned batches whose phases haven't been written to the row yet.
      if (b.hatchDate && b.breed) {
        const live = calcTimeline(b.hatchDate, b.breed, b.processingDate, b.growoutWeeks, b.brooderDays);
        if (live) {
          bump(live.brooderOut);
          bump(live.schoonerOut);
        }
      }
    }
    for (const lb of layerBatches) {
      if (lb.status !== 'active') continue;
      if (lb.name === 'Retirement Home') continue;
      const bs = lb.brooder_entry_date;
      if (bs) bump(lb.brooder_exit_date || toISO(addDays(new Date(bs + 'T12:00:00'), 21)));
      const ss = lb.schooner_entry_date;
      if (ss) bump(lb.schooner_exit_date || toISO(addDays(new Date(ss + 'T12:00:00'), 119)));
    }
    return latest;
  }, [batches, layerBatches, today]);

  const viewport = poultryTimelineViewport({
    today,
    start: tlStart,
    dataEnd: toISO(addDays(latestEndISO, 30)),
    contentWidth,
    phone,
  });
  const tlEndISO = viewport.end;
  const tlS = new Date(tlStart + 'T12:00:00');
  const tlE = new Date(tlEndISO + 'T12:00:00');
  const totalDays = viewport.totalDays;
  const weeksShown = Math.max(1, Math.ceil(totalDays / 7));
  const pct = (iso: string | null | undefined) =>
    ((poultryTimelineDay(String(iso)) - poultryTimelineDay(tlStart)) / totalDays) * 100;
  const wkHdrs = Array.from({length: weeksShown}, (_, i) => poultryTimelineDate(poultryTimelineDay(tlStart) + i * 7));
  const monthHeaders = poultryTimelineMonths(tlStart, tlEndISO);

  // Measure this chart, including a narrower embedded preview, rather than the
  // browser width. clientWidth excludes borders; subtract padding as well.
  React.useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const measure = () => {
      const style = getComputedStyle(el);
      setContentWidth(el.clientWidth - (parseFloat(style.paddingLeft) || 0) - (parseFloat(style.paddingRight) || 0));
    };
    measure();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', measure);
      return () => window.removeEventListener('resize', measure);
    }
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [timelineResource.loading, timelineResource.error]);

  React.useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el || contentWidth <= POULTRY_TIMELINE_GUTTER) return;
    // Preserve the date at the visible canvas's left edge after a resize or
    // range refresh. Only the first measured layout chooses the opening date.
    const target =
      scrollGeometry.current && viewedDay.current != null
        ? (viewedDay.current - poultryTimelineDay(tlStart)) * viewport.dayWidth
        : viewport.initialScrollLeft;
    el.scrollLeft = Math.max(0, Math.min(viewport.width - contentWidth, target));
    scrollGeometry.current = {start: tlStart, dayWidth: viewport.dayWidth};
    viewedDay.current = poultryTimelineDay(tlStart) + el.scrollLeft / viewport.dayWidth;
  }, [
    contentWidth,
    tlStart,
    viewport.dayWidth,
    viewport.width,
    viewport.initialScrollLeft,
    timelineResource.loading,
    timelineResource.error,
  ]);

  function rememberViewedDate() {
    const el = scrollRef.current;
    const geometry = scrollGeometry.current;
    if (el && geometry) viewedDay.current = poultryTimelineDay(geometry.start) + el.scrollLeft / geometry.dayWidth;
  }

  // Three states where there used to be one. Both directories arrived with the
  // boot payload, so "empty" and "not loaded yet" were the same frame and
  // "unreachable" could not happen. A route-local read makes all three real.
  //
  // These are EARLY RETURNS rather than blocks inside the Gantt, and that is
  // deliberate: the range this view derives is data-dependent, so rendering the
  // week headers before the batches arrive would publish a first-paint range
  // computed from an empty directory and then silently replace it. Holding the
  // Gantt back until the read settles reproduces exactly what the boot gate
  // used to guarantee.
  if (timelineResource.loading) {
    return (
      <div style={{minHeight: embedded ? undefined : '100vh', background: 'var(--bg-page)'}}>
        {!embedded && <Header />}
        <div
          data-broiler-timeline-loading
          style={{textAlign: 'center', padding: '3rem', color: 'var(--ink-faint)', fontSize: 13}}
        >
          Loading timeline…
        </div>
      </div>
    );
  }

  // An `if`, not the `{Boolean(error) && …}` form LayersView uses inside its
  // JSX: a statement condition takes `unknown` fine, so the wrapper that makes
  // the expression form a valid ReactNode would just be redundant here.
  if (timelineResource.error) {
    return (
      <div style={{minHeight: embedded ? undefined : '100vh', background: 'var(--bg-page)'}}>
        {!embedded && <Header />}
        <div
          data-broiler-timeline-load-error
          style={{textAlign: 'center', padding: '3rem', color: 'var(--danger)', fontSize: 13}}
        >
          Could not load the timeline.{' '}
          <Button type="button" onClick={() => timelineResource.refetch()}>
            Retry
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div style={{minHeight: embedded ? undefined : '100vh', background: 'var(--bg-page)'}}>
      {!embedded && <Header />}
      <div style={{padding: embedded ? 0 : '1rem', maxWidth: embedded ? undefined : 1180, margin: '0 auto'}}>
        {!embedded && (
          <h1 className="poultry-page-title" style={{marginBottom: 16}}>
            Poultry Timeline
          </h1>
        )}
        {!embedded && canAdd && openAdd && (
          <div style={{display: 'flex', justifyContent: 'flex-end', marginBottom: 12}}>
            <Button variant="primary" type="button" onClick={openAdd} style={{width: 'auto'}}>
              + Add Batch
            </Button>
          </div>
        )}
        {/* Gantt */}
        <div ref={scrollRef} onScroll={rememberViewedDate} style={{...S.card, overflowX: 'auto'}}>
          <div
            style={{width: `${viewport.width}px`, position: 'relative'}}
            data-gantt="1"
            data-range-start={tlStart}
            data-range-end={tlEndISO}
            data-day-width={viewport.dayWidth}
            data-window-start={viewport.windowStart}
            data-window-end={viewport.windowEnd}
          >
            {/* Floating tooltip */}
            {tooltip &&
              (() => {
                if (tooltip.type === 'layer') {
                  const lb = tooltip.lb;
                  const bar = tooltip.bar;
                  return (
                    <div
                      role="tooltip"
                      id="poultry-timeline-tooltip"
                      style={{
                        position: 'fixed',
                        left: tooltip.vx,
                        top: tooltip.vy,
                        transform: 'translate(-50%,-100%)',
                        zIndex: 'var(--z-menu)',
                        pointerEvents: 'none',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                      }}
                    >
                      <div
                        style={{
                          background: 'var(--surface)',
                          border: '1px solid var(--border)',
                          color: 'var(--text-primary)',
                          borderRadius: 10,
                          padding: '9px 12px',
                          fontSize: 12,
                          boxShadow: '0 4px 16px rgba(0,0,0,.12)',
                          minWidth: 180,
                          maxWidth: 240,
                        }}
                      >
                        <div style={{fontWeight: 700, fontSize: 13, marginBottom: 5, color: 'var(--text-primary)'}}>
                          {lb.name}
                          {' \u00b7 '}
                          {bar.label}
                        </div>
                        <div style={{display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '3px 10px'}}>
                          {lb.original_count && (
                            <>
                              <span style={{color: '#68707A'}}>Birds:</span>
                              <span style={{color: 'var(--text-primary)'}}>{lb.original_count.toLocaleString()}</span>
                            </>
                          )}
                          <span style={{color: '#68707A'}}>{bar.label}:</span>
                          <span style={{color: 'var(--text-primary)'}}>
                            {fmtS(bar.start)} {'\u2192'} {fmtS(bar.end)}
                          </span>
                          {bar.resName && (
                            <>
                              <span style={{color: '#68707A'}}>Using:</span>
                              <span style={{color: 'var(--text-primary)'}}>{bar.resName}</span>
                            </>
                          )}
                          {lb.brooder_entry_date && (
                            <>
                              <span style={{color: '#68707A'}}>Brooder in:</span>
                              <span style={{color: 'var(--text-primary)'}}>{fmtS(lb.brooder_entry_date)}</span>
                            </>
                          )}
                          {lb.schooner_exit_date && (
                            <>
                              <span style={{color: '#68707A'}}>Schooner out:</span>
                              <span style={{color: 'var(--text-primary)'}}>{fmtS(lb.schooner_exit_date)}</span>
                            </>
                          )}
                        </div>
                      </div>
                      <div
                        style={{
                          width: 0,
                          height: 0,
                          borderLeft: '7px solid transparent',
                          borderRight: '7px solid transparent',
                          borderTop: '7px solid var(--border)',
                        }}
                      />
                    </div>
                  );
                }
                const b = tooltip.batch;
                const isBrooder = tooltip.resType === 'brooder';
                const live = calcTimeline(b.hatchDate, b.breed, b.processingDate, b.growoutWeeks, b.brooderDays);
                const brooderIn = b.brooderIn || (live ? live.brooderIn : null);
                const brooderOut = b.brooderOut || (live ? live.brooderOut : null);
                const schoonerIn = live ? live.schoonerIn : b.schoonerIn;
                const schoonerOut = live ? live.schoonerOut : b.schoonerOut;
                return (
                  <div
                    role="tooltip"
                    id="poultry-timeline-tooltip"
                    style={{
                      position: 'fixed',
                      left: tooltip.vx,
                      top: tooltip.vy,
                      transform: 'translate(-50%,-100%)',
                      zIndex: 'var(--z-menu)',
                      pointerEvents: 'none',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                    }}
                  >
                    <div
                      style={{
                        background: 'var(--surface)',
                        border: '1px solid var(--border)',
                        color: 'var(--text-primary)',
                        borderRadius: 10,
                        padding: '9px 12px',
                        fontSize: 12,
                        boxShadow: '0 4px 16px rgba(0,0,0,.12)',
                        minWidth: 180,
                        maxWidth: 240,
                      }}
                    >
                      <div style={{fontWeight: 700, fontSize: 13, marginBottom: 5, color: 'var(--text-primary)'}}>
                        {b.name}
                      </div>
                      <div style={{display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '3px 10px'}}>
                        <span style={{color: '#68707A'}}>Breed:</span>
                        <span style={{color: 'var(--text-primary)'}}>{breedLabel(b.breed)}</span>
                        <span style={{color: '#68707A'}}>Hatchery:</span>
                        <span style={{color: 'var(--text-primary)'}}>{b.hatchery}</span>
                        <span style={{color: '#68707A'}}>Birds:</span>
                        <span style={{color: 'var(--text-primary)'}}>{b.birdCount}</span>
                        {isBrooder && (
                          <>
                            <span style={{color: '#68707A'}}>Brooder {b.brooder}:</span>
                            <span style={{color: 'var(--text-primary)'}}>
                              {fmtS(brooderIn) + ' \u2192 ' + fmtS(brooderOut)}
                            </span>
                          </>
                        )}
                        {!isBrooder && (
                          <>
                            <span style={{color: '#68707A'}}>Schooner {b.schooner}:</span>
                            <span style={{color: 'var(--text-primary)'}}>
                              {fmtS(schoonerIn) + ' \u2192 ' + fmtS(schoonerOut)}
                            </span>
                          </>
                        )}
                        {b.processingDate && (
                          <>
                            <span style={{color: '#68707A'}}>Processing:</span>
                            <span style={{color: 'var(--text-primary)'}}>{fmt(b.processingDate)}</span>
                          </>
                        )}
                      </div>
                    </div>
                    <div
                      style={{
                        width: 0,
                        height: 0,
                        borderLeft: '7px solid transparent',
                        borderRight: '7px solid transparent',
                        borderTop: '7px solid var(--border)',
                      }}
                    />
                  </div>
                );
              })()}

            {/* Calendar months and weeks share the bars' date scale. */}
            <div style={{display: 'flex', borderBottom: '1px solid var(--border)'}}>
              <div
                aria-hidden="true"
                style={{
                  width: POULTRY_TIMELINE_GUTTER,
                  flexShrink: 0,
                  position: 'sticky',
                  left: 0,
                  zIndex: 10,
                  borderRight: '1px solid var(--border)',
                  background: 'var(--surface-2)',
                }}
              />
              <div style={{flex: 1, position: 'relative', height: 28, overflow: 'hidden'}}>
                {monthHeaders.map((month, i) => (
                  <div
                    key={month.date}
                    data-month-header="1"
                    data-iso={month.date}
                    style={{
                      position: 'absolute',
                      left: `${pct(month.date)}%`,
                      width: `${pct(monthHeaders[i + 1]?.date || tlEndISO) - pct(month.date)}%`,
                      height: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      paddingLeft: 5,
                      borderLeft: '1px solid var(--border)',
                      fontSize: 11,
                      fontWeight: 600,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                    }}
                  >
                    {month.label}
                  </div>
                ))}
              </div>
            </div>
            <div style={{display: 'flex', borderBottom: '1px solid var(--border)'}}>
              <div
                style={{
                  width: POULTRY_TIMELINE_GUTTER,
                  flexShrink: 0,
                  padding: '6px 10px',
                  fontSize: 10,
                  fontWeight: 600,
                  color: 'var(--ink-faint)',
                  borderRight: '1px solid var(--border)',
                  background: 'var(--surface-2)',
                  position: 'sticky',
                  left: 0,
                  zIndex: 10,
                }}
              >
                Resource
              </div>
              <div style={{flex: 1, position: 'relative', height: 26, overflow: 'hidden'}}>
                {wkHdrs.map((w, i) => {
                  const date = new Date(w + 'T12:00:00');
                  const isNew = date.getDate() <= 7;
                  return (
                    <div
                      key={i}
                      data-week-header="1"
                      data-iso={w}
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: `${pct(w)}%`,
                        height: '100%',
                        borderLeft: `1px solid ${isNew ? '#aaa' : '#eee'}`,
                        paddingLeft: 3,
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      <span
                        style={{
                          fontSize: 9,
                          whiteSpace: 'nowrap',
                          color: isNew ? '#444' : '#bbb',
                          fontWeight: isNew ? 600 : 400,
                        }}
                      >
                        {isNew ? date.toLocaleDateString('en-US', {month: 'short', day: 'numeric'}) : date.getDate()}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Today line overlay — calculated in row */}
            {RESOURCES.map((res, ri) => {
              // Visual row separation (presentational only): one strong divider
              // at the brooder -> schooner boundary and an alternating light
              // fill on schooner rows for horizontal scanning. See
              // src/lib/broilerTimelineRows.js.
              const {boundaryTop, fill} = timelineRowSeparation(ri);
              const rowBg = fill === 'shaded' ? 'var(--surface-2)' : fill === 'plain' ? 'var(--bg-card)' : null;
              const rowBatches = batches.filter((b) => {
                if (res.type === 'brooder' && b.brooder !== res.id) return false;
                if (res.type === 'schooner' && b.schooner !== res.id) return false;
                const s = res.type === 'brooder' ? b.brooderIn : b.schoonerIn;
                const e = res.type === 'brooder' ? b.brooderOut : b.schoonerOut;
                return new Date(e + 'T12:00:00') >= tlS && new Date(s + 'T12:00:00') <= tlE;
              });
              const todayPct = pct(todayISO());
              return (
                <div
                  key={res.label}
                  data-resource-row="1"
                  data-resource-type={res.type}
                  data-resource-label={res.label}
                  data-row-fill={fill}
                  data-row-divider={boundaryTop ? '1' : undefined}
                  style={{display: 'flex', borderTop: boundaryTop ? '2px solid var(--border)' : '1px solid #eee'}}
                >
                  <div
                    data-row-region="label"
                    style={{
                      width: POULTRY_TIMELINE_GUTTER,
                      flexShrink: 0,
                      padding: '0 10px',
                      display: 'flex',
                      alignItems: 'center',
                      height: 44,
                      fontSize: 11,
                      fontWeight: 600,
                      color: 'var(--ink-muted)',
                      borderRight: '1px solid var(--border)',
                      // Opaque fill so bars scrolling under the sticky label stay
                      // hidden; schooner rows alternate, brooders keep the sidebar.
                      background: rowBg || 'var(--surface-2)',
                      position: 'sticky',
                      left: 0,
                      zIndex: 10,
                    }}
                  >
                    {res.label}
                  </div>
                  <div
                    data-row-region="grid"
                    style={{flex: 1, position: 'relative', height: 44, background: rowBg || undefined}}
                  >
                    {/* week grid lines */}
                    {wkHdrs.map((week, i) => (
                      <div
                        key={i}
                        data-week-grid="1"
                        data-iso={week}
                        style={{
                          position: 'absolute',
                          top: 0,
                          left: `${pct(week)}%`,
                          height: '100%',
                          borderLeft: '1px solid #f0f0f0',
                          pointerEvents: 'none',
                        }}
                      />
                    ))}
                    {/* today line */}
                    {todayPct >= 0 && todayPct <= 100 && (
                      <div
                        data-today-line="1"
                        style={{
                          position: 'absolute',
                          top: 0,
                          left: `${todayPct}%`,
                          height: '100%',
                          borderLeft: '2px solid rgba(24,122,84,.4)',
                          pointerEvents: 'none',
                          zIndex: 2,
                        }}
                      />
                    )}
                    {/* batch bars (broiler) */}
                    {rowBatches.map((b) => {
                      const s = res.type === 'brooder' ? b.brooderIn : b.schoonerIn;
                      const e = res.type === 'brooder' ? b.brooderOut : b.schoonerOut;
                      const left = Math.max(0, pct(s));
                      const right = Math.min(100, pct(e));
                      const w = right - left;
                      if (w < 0.3) return null;
                      const C = getBatchDisplayColor(b);
                      // "Only what's in a pen NOW is solid" (the Pig Timeline
                      // pattern). A bar whose span contains today is the birds'
                      // present location and reads at full strength; the rest
                      // of the same batch's run — where it has been, where it
                      // is going — steps back to a tint of the same hue, so
                      // one glance answers "what is in this pen today?" while
                      // the batch stays one colour end to end.
                      const occupiedNow = !!s && !!e && s <= today && today <= e;
                      const isHovered =
                        tooltip && tooltip.type !== 'layer' && tooltip.id === b.id && tooltip.type === res.type;
                      return (
                        <div
                          role="button"
                          tabIndex={0}
                          aria-label={`${b.name || 'Batch'} · ${res.label}`}
                          aria-describedby="poultry-timeline-tooltip"
                          onFocus={(e) => {
                            const barRect = e.currentTarget.getBoundingClientRect();
                            setTooltip({
                              id: b.id,
                              type: res.type,
                              batch: b,
                              resType: res.type,
                              vx: Math.max(128, Math.min(window.innerWidth - 128, barRect.left + barRect.width / 2)),
                              vy: Math.max(190, barRect.top - 10),
                            });
                          }}
                          onPointerDown={(e) => {
                            if (e.pointerType !== 'touch') return;
                            const barRect = e.currentTarget.getBoundingClientRect();
                            setTooltip({
                              id: b.id,
                              type: res.type,
                              batch: b,
                              resType: res.type,
                              vx: Math.max(128, Math.min(window.innerWidth - 128, barRect.left + barRect.width / 2)),
                              vy: Math.max(190, barRect.top - 10),
                            });
                          }}
                          onBlur={() => setTooltip(null)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              e.currentTarget.click();
                            }
                            if (e.key === 'Escape') setTooltip(null);
                          }}
                          key={b.id}
                          onClick={() => openBroilerBatch(b)}
                          onMouseEnter={(e) => {
                            const barRect = e.currentTarget.getBoundingClientRect();
                            setTooltip({
                              id: b.id,
                              type: res.type,
                              batch: b,
                              resType: res.type,
                              vx: Math.max(128, Math.min(window.innerWidth - 128, barRect.left + barRect.width / 2)),
                              vy: Math.max(190, barRect.top - 10),
                            });
                          }}
                          onMouseLeave={() => setTooltip(null)}
                          style={{
                            position: 'absolute',
                            left: left + '%',
                            width: w + '%',
                            top: 6,
                            bottom: 6,
                            borderRadius: 10,
                            cursor: embedded ? 'default' : 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            padding: '0 6px',
                            overflow: 'hidden',
                            zIndex: isHovered ? 10 : 1,
                            background: occupiedNow ? C.bg : `color-mix(in srgb, ${C.bg} 38%, var(--surface))`,
                            color: occupiedNow ? C.tx : 'var(--ink)',
                            border:
                              '1px solid ' + (occupiedNow ? C.bd : `color-mix(in srgb, ${C.bd} 55%, var(--surface))`),
                            outline:
                              b.conflictOverride && b.status !== 'processed'
                                ? '2px dashed #E24B4A'
                                : isHovered
                                  ? '2px solid rgba(0,0,0,.25)'
                                  : 'none',
                            outlineOffset: '-2px',
                          }}
                        >
                          <span
                            style={{
                              fontSize: 10,
                              fontWeight: 600,
                              overflow: 'hidden',
                              whiteSpace: 'nowrap',
                              textOverflow: 'ellipsis',
                            }}
                          >
                            {(b.conflictOverride && b.status !== 'processed' ? '\u26a0 ' : '') + b.name}
                          </span>
                        </div>
                      );
                    })}
                    {/* Layer bars merged into the same broiler resource row */}
                    {(layerBatches || [])
                      .filter((lb) => {
                        if (lb.status !== 'active') return false;
                        if (lb.name === 'Retirement Home') return false;
                        // Strip prefix to compare with res.id
                        if (res.type === 'brooder') {
                          const lbId = (lb.brooder_name || '').replace(/^Brooder\s*/i, '').trim();
                          if (lbId !== res.id) return false;
                          if (!lb.brooder_entry_date) return false;
                          const lbStart = lb.brooder_entry_date;
                          const lbEnd = lb.brooder_exit_date || toISO(addDays(new Date(lbStart + 'T12:00:00'), 21));
                          return new Date(lbEnd + 'T12:00:00') >= tlS && new Date(lbStart + 'T12:00:00') <= tlE;
                        } else {
                          const lbId = (lb.schooner_name || '').replace(/^Schooner\s*/i, '').trim();
                          if (lbId !== res.id) return false;
                          if (!lb.schooner_entry_date) return false;
                          const lbStart = lb.schooner_entry_date;
                          const lbEnd = lb.schooner_exit_date || toISO(addDays(new Date(lbStart + 'T12:00:00'), 119));
                          return new Date(lbEnd + 'T12:00:00') >= tlS && new Date(lbStart + 'T12:00:00') <= tlE;
                        }
                      })
                      .map((lb) => {
                        const isB = res.type === 'brooder';
                        const lbStart = isB ? lb.brooder_entry_date : lb.schooner_entry_date;
                        const lbEnd = isB
                          ? lb.brooder_exit_date || toISO(addDays(new Date(lbStart + 'T12:00:00'), 21))
                          : lb.schooner_exit_date || toISO(addDays(new Date(lbStart + 'T12:00:00'), 119));
                        const left = Math.max(0, pct(lbStart));
                        const right = Math.min(100, pct(lbEnd));
                        const w = right - left;
                        if (w < 0.3) return null;
                        const C = getBatchColor(lb.name);
                        // Same rule as the broiler bars: solid only where the
                        // birds are today.
                        const lbOccupiedNow = !!lbStart && !!lbEnd && lbStart <= today && today <= lbEnd;
                        const isHov =
                          tooltip &&
                          tooltip.type === 'layer' &&
                          tooltip.lbId === lb.id &&
                          tooltip.barKey === (isB ? 'b' : 's');
                        return (
                          <div
                            role="button"
                            tabIndex={0}
                            aria-label={`${lb.name || 'Layer Batch'} · ${isB ? 'Brooder' : 'Schooner'}`}
                            aria-describedby="poultry-timeline-tooltip"
                            onFocus={(e) => {
                              const r = e.currentTarget.getBoundingClientRect();
                              setTooltip({
                                lbId: lb.id,
                                barKey: isB ? 'b' : 's',
                                lb,
                                bar: {
                                  label: isB ? 'Brooder' : 'Schooner',
                                  start: lbStart,
                                  end: lbEnd,
                                  resName: isB ? lb.brooder_name : lb.schooner_name,
                                },
                                vx: Math.max(128, Math.min(window.innerWidth - 128, r.left + r.width / 2)),
                                vy: Math.max(190, r.top - 10),
                                type: 'layer',
                              });
                            }}
                            onPointerDown={(e) => {
                              if (e.pointerType !== 'touch') return;
                              const r = e.currentTarget.getBoundingClientRect();
                              setTooltip({
                                lbId: lb.id,
                                barKey: isB ? 'b' : 's',
                                lb,
                                bar: {
                                  label: isB ? 'Brooder' : 'Schooner',
                                  start: lbStart,
                                  end: lbEnd,
                                  resName: isB ? lb.brooder_name : lb.schooner_name,
                                },
                                vx: Math.max(128, Math.min(window.innerWidth - 128, r.left + r.width / 2)),
                                vy: Math.max(190, r.top - 10),
                                type: 'layer',
                              });
                            }}
                            onBlur={() => setTooltip(null)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                e.currentTarget.click();
                              }
                              if (e.key === 'Escape') setTooltip(null);
                            }}
                            key={'lb-' + lb.id + '-' + res.id}
                            onClick={() => {
                              if (embedded) return;
                              if (setPendingEdit) setPendingEdit({id: lb.id, viewName: 'layerbatches'});
                              setView && setView('layerbatches');
                            }}
                            onMouseEnter={(e) => {
                              const r = e.currentTarget.getBoundingClientRect();
                              setTooltip({
                                lbId: lb.id,
                                barKey: isB ? 'b' : 's',
                                lb,
                                bar: {
                                  label: isB ? 'Brooder' : 'Schooner',
                                  start: lbStart,
                                  end: lbEnd,
                                  resName: isB ? lb.brooder_name : lb.schooner_name,
                                },
                                vx: Math.max(128, Math.min(window.innerWidth - 128, r.left + r.width / 2)),
                                vy: Math.max(190, r.top - 10),
                                type: 'layer',
                              });
                            }}
                            onMouseLeave={() => setTooltip(null)}
                            style={{
                              position: 'absolute',
                              left: left + '%',
                              width: w + '%',
                              top: 6,
                              bottom: 6,
                              borderRadius: 10,
                              cursor: embedded ? 'default' : 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              padding: '0 6px',
                              overflow: 'hidden',
                              zIndex: isHov ? 10 : 1,
                              background: lbOccupiedNow ? C.bg : `color-mix(in srgb, ${C.bg} 38%, var(--surface))`,
                              color: lbOccupiedNow ? C.tx : 'var(--ink)',
                              // Layer batches are marked by a dark outline, not
                              // the old amber: Q42 dropped the emoji, and D02
                              // keeps status hues out of series decoration. The
                              // outline stays full strength either way — it is
                              // identity, not occupancy.
                              border: '2px solid var(--ink)',
                              outline: isHov ? '2px solid rgba(0,0,0,.25)' : 'none',
                              outlineOffset: '-2px',
                            }}
                          >
                            <span
                              style={{
                                fontSize: 10,
                                fontWeight: 600,
                                overflow: 'hidden',
                                whiteSpace: 'nowrap',
                                textOverflow: 'ellipsis',
                              }}
                            >
                              {lb.name}
                            </span>
                          </div>
                        );
                      })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Legend — labels in gray ink under the chart. Every bar is
            direct-labeled with its batch name, so the chips carry only the
            two encodings the old paragraph named (a per-batch chip row
            would list the whole blob and, at six colors, could not decode
            color to batch anyway). Q42: no emoji. */}
        <div style={{display: 'flex', gap: 12, marginTop: 10, flexWrap: 'wrap', alignItems: 'center'}}>
          <span style={{display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11, color: 'var(--ink-muted)'}}>
            <span
              style={{
                display: 'inline-block',
                width: 10,
                height: 10,
                borderRadius: 2 /* radius-allow: 10px legend swatch */,
                background: 'var(--surface-2)',
                border: '2px solid var(--ink)',
              }}
            />
            dark outline = layer batch
          </span>
          <span style={{display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11, color: 'var(--ink-muted)'}}>
            <span
              style={{
                display: 'inline-block',
                width: 10,
                height: 10,
                borderRadius: 2 /* radius-allow: 10px legend swatch */,
                background: 'var(--group-1)',
              }}
            />
            solid = in that pen today
          </span>
          <span style={{display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11, color: 'var(--ink-muted)'}}>
            <span
              style={{
                display: 'inline-block',
                width: 10,
                height: 10,
                borderRadius: 2 /* radius-allow: 10px legend swatch */,
                background: 'color-mix(in srgb, var(--group-1) 38%, var(--surface))',
              }}
            />
            tinted = same batch, before or after
          </span>
          <span style={{display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11, color: 'var(--ink-muted)'}}>
            <span style={{display: 'inline-block', width: 3, height: 12, background: 'rgba(24,122,84,.8)'}} />
            green line = today
          </span>
          {!embedded && <span style={{fontSize: 11, color: 'var(--ink-faint)'}}>click any bar to edit</span>}
        </div>

        {batches.length === 0 && (
          <div style={{textAlign: 'center', padding: '3rem', color: 'var(--ink-faint)', fontSize: 13}}>
            {embedded || !canAdd || !openAdd ? 'No batches yet' : 'No batches yet — click "+ Add Batch" to get started'}
          </div>
        )}
      </div>
      {!embedded &&
        showForm &&
        !editId &&
        upd &&
        closeForm &&
        submit &&
        del &&
        parseProcessorXlsx &&
        persistDocuments && (
          <BatchForm
            hostedByPage
            timelinePreview={<BroilerTimelineView embedded />}
            upd={upd}
            closeForm={closeForm}
            submit={submit}
            del={del}
            openEdit={openEdit ?? (() => {})}
            parseProcessorXlsx={parseProcessorXlsx}
            persistDocuments={persistDocuments}
          />
        )}
    </div>
  );
}
